// Alert dialog for confirming a saved-method Rapid Reload purchase.

import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Spinner } from '@/components/ui/spinner';
import { useProductCheckout } from '@/hooks';
import type { AnalyticsSurface } from '@/types/analytics';
import type { ProductDocument } from '@airhead-games/shared/types';

type ConfirmBuyLoonsProps = {
  open: boolean;
  product: ProductDocument;
  savedPaymentMethodId: string;
  loonCount: number | null;
  onCancel: () => void;
  onSuccess: () => void;
  analyticsSurface?: AnalyticsSurface;
};

const ConfirmBuyLoons: React.FC<ConfirmBuyLoonsProps> = ({
  open,
  product,
  savedPaymentMethodId,
  loonCount,
  onCancel,
  onSuccess,
  analyticsSurface = 'library',
}) => {
  const { checkoutProduct, activeCheckoutPriceId, checkoutError } =
    useProductCheckout();
  const isCheckoutLoading = activeCheckoutPriceId === product.stripePriceId;

  const handleConfirm = async () => {
    const result = await checkoutProduct({
      product,
      trackingLabel: 'Rapid_Reload_Buy_Loons_Confirm',
      trackingContext: {
        surface: analyticsSurface,
        flow_type: 'quick_purchase',
        page_context: 'buy_loons_confirmation',
      },
      savedPaymentMethodId,
      onQuickPurchaseSuccess: onSuccess,
      quickPurchaseSuccessDurationMs: 1500,
    });

    if (result === 'redirected') {
      onSuccess();
    }
  };

  const handleConfirmClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    void handleConfirm();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isCheckoutLoading) {
      onCancel();
    }
  };

  const purchaseDescription =
    loonCount != null
      ? `Top up your balance to play more games. Confirm to buy ${loonCount} Loons with your saved payment method.`
      : `Top up your balance to play more games. Confirm to buy ${product.name} with your saved payment method.`;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Rapid Reload Purchase</AlertDialogTitle>
          <AlertDialogDescription>{purchaseDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        {checkoutError ? (
          <p className="text-sm text-destructive" role="alert">
            {checkoutError}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={isCheckoutLoading}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            type="button"
            disabled={isCheckoutLoading}
            onClick={handleConfirmClick}
          >
            {isCheckoutLoading ? (
              <Spinner className="size-4 text-primary-foreground" />
            ) : (
              'Confirm'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmBuyLoons;
